/* ============================================================
   CELESTIAL VAULT — motion layer
   Lenis smooth scroll · scroll reveal · page-transition veil ·
   subtle card tilt + parallax. All gated on prefers-reduced-motion.
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var inDesignMode = window.Shopify && window.Shopify.designMode;
  var cfg = window.CV || { smoothScroll: true, pageTransitions: true };

  /* ---- Lenis smooth inertia scroll ---------------------- */
  function initLenis() {
    if (reduceMotion || !cfg.smoothScroll || typeof window.Lenis === 'undefined') return;
    var lenis = new window.Lenis({
      lerp: 0.1,
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.6,
    });
    window.__cvLenis = lenis;
    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    // html{height:100%} means Lenis' own ResizeObserver (which watches the
    // documentElement box) never fires, so content that loads after init —
    // review widgets, product recommendations — grows the page while the
    // scroll limit stays stale, leaving a wheel-unreachable dead zone at the
    // bottom. Watch the body, which does grow, and re-measure.
    function refreshLimit() { lenis.resize(); }
    if ('ResizeObserver' in window) {
      new ResizeObserver(refreshLimit).observe(document.body);
    }
    // Frame-independent fallbacks: app embeds (reviews, recommendations)
    // can inject content well after load, and ResizeObserver delivery is
    // tied to rendering frames.
    if (document.readyState === 'complete') {
      refreshLimit();
    } else {
      window.addEventListener('load', refreshLimit);
    }
    setTimeout(refreshLimit, 1500);
    setTimeout(refreshLimit, 4000);
    setTimeout(refreshLimit, 8000);

    // Keep anchor links working through Lenis
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute('href');
      if (id.length < 2) return;
      var el = document.querySelector(id);
      if (el) { e.preventDefault(); lenis.scrollTo(el, { offset: -80 }); }
    });
  }

  /* ---- Scroll reveal ------------------------------------ */
  function initReveal() {
    var els = document.querySelectorAll('.cv-reveal');
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('cv-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var delay = parseInt(el.getAttribute('data-cv-delay') || '0', 10);
        setTimeout(function () { el.classList.add('cv-in'); }, delay);
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---- Stagger helper: tag grid children as reveals ----- */
  function autoStagger() {
    document.querySelectorAll('[data-cv-stagger]').forEach(function (grid) {
      var kids = grid.children;
      for (var i = 0; i < kids.length; i++) {
        kids[i].classList.add('cv-reveal');
        kids[i].setAttribute('data-cv-delay', String((i % 4) * 70));
      }
    });
  }

  /* ---- Page-transition veil ----------------------------- */
  function initVeil() {
    if (reduceMotion || !cfg.pageTransitions) return;
    var veil = document.createElement('div');
    veil.className = 'cv-veil';
    document.body.appendChild(veil);

    document.addEventListener('click', function (e) {
      var a = e.target.closest('a');
      if (!a) return;
      var href = a.getAttribute('href');
      if (!href || a.target === '_blank' || a.hasAttribute('download')) return;
      if (href.charAt(0) === '#' || href.indexOf('mailto:') === 0 || href.indexOf('tel:') === 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey) return;
      // same-origin navigations only
      var url;
      try { url = new URL(href, window.location.href); } catch (_) { return; }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      document.documentElement.classList.add('cv-leaving');
    });

    // Restore on back/forward (bfcache)
    window.addEventListener('pageshow', function () {
      document.documentElement.classList.remove('cv-leaving');
    });
  }

  /* ---- Subtle card tilt (cap ~6deg) --------------------- */
  function initTilt() {
    // No pointer-capability gate: pointer events cover mouse, pen and touch,
    // so tilt works on every device instead of guessing hardware.
    if (reduceMotion) return;
    document.querySelectorAll('.card-wrapper').forEach(function (card) {
      var inner = card.querySelector('.card');
      if (!inner) return;
      inner.style.transformStyle = 'preserve-3d';
      inner.style.transition = 'transform 0.3s var(--cv-ease)';
      card.addEventListener('pointermove', function (e) {
        var r = card.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        inner.style.transform = 'perspective(900px) rotateY(' + (px * 6) + 'deg) rotateX(' + (-py * 6) + 'deg)';
      });
      card.addEventListener('pointerleave', function () {
        inner.style.transform = '';
      });
    });
  }

  /* ---- Lightweight hero parallax ------------------------ */
  function initParallax() {
    if (reduceMotion) return;
    var nodes = document.querySelectorAll('[data-cv-parallax]');
    if (!nodes.length) return;
    var ticking = false;
    function update() {
      var vh = window.innerHeight;
      nodes.forEach(function (n) {
        var speed = parseFloat(n.getAttribute('data-cv-parallax')) || 0.15;
        var rect = n.getBoundingClientRect();
        var offset = (rect.top + rect.height / 2 - vh / 2) * -speed;
        n.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
      });
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ---- Hero masked word reveal (Jitter-style) ----------- */
  function wrapWords(text, gradient) {
    var frag = document.createDocumentFragment();
    text.split(/(\s+)/).forEach(function (tok) {
      if (tok === '') return;
      if (!tok.trim()) { frag.appendChild(document.createTextNode(tok)); return; }
      var outer = document.createElement('span');
      outer.className = 'cv-word';
      var inner = document.createElement('span');
      inner.className = 'cv-word__in' + (gradient ? ' cv-grad-text' : '');
      inner.textContent = tok;
      outer.appendChild(inner);
      frag.appendChild(outer);
    });
    return frag;
  }

  function initHeroReveal() {
    var title = document.querySelector('.cv-hero__title');
    if (!title || title.dataset.cvSplit === '1') return;
    title.dataset.cvSplit = '1';
    // The title also carries .cv-reveal — let the word reveal own it instead.
    title.classList.remove('cv-reveal');
    title.classList.add('cv-in');

    if (reduceMotion) return; // leave text as-is, fully visible

    var nodes = Array.prototype.slice.call(title.childNodes);
    nodes.forEach(function (n) {
      if (n.nodeType === 3) {
        if (n.textContent.trim()) title.replaceChild(wrapWords(n.textContent, false), n);
      } else if (n.nodeType === 1) {
        var grad = n.classList && n.classList.contains('cv-grad-text');
        title.replaceChild(wrapWords(n.textContent, grad), n);
      }
    });

    var words = title.querySelectorAll('.cv-word__in');
    words.forEach(function (w, i) { w.style.transitionDelay = (120 + i * 70) + 'ms'; });
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { title.classList.add('cv-words-in'); });
    });
  }

  function boot() {
    initHeroReveal();
    autoStagger();
    initReveal();
    initVeil();
    initTilt();
    initParallax();
    if (!inDesignMode) initLenis();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-run reveal/stagger when sections re-render in the theme editor
  document.addEventListener('shopify:section:load', function () {
    autoStagger();
    initReveal();
    initTilt();
  });
})();
